import { Injectable } from '@angular/core';
import { BookingSlot } from '../models/booking-slot';
import { Observable, of, Subscriber } from 'rxjs';
import { BookingSlotSelectPolicyEnum } from './booking-slot-select-policy.enum';
import { environment } from '../../../../environments/environment';
import { OrderTypeEnum } from '../../order/models/order-type.enum';
import { BookingOrderService } from './booking-order.service';
import { CalendarService } from '../../general/services/calendar.service';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { BookingSlotChangeStateService } from '../../partner/services/booking-slot-change-state-service.service';
import { expand, filter, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { OrderLineType } from '../../order/models/order-line-type';
import { GlobalSettings } from 'src/environments/global-settings';

@Injectable()
export class BookingSlotStateService extends CalendarService {

  private _defaultTimeSlotsLength = 60; // minutes
  private _defaultJumpersCount = 1;
  private _defaultMaxSlotsCountToShowTime = 32;
  private _selectedDate: Date;
  // private _selectedDate: Date = GlobalSettings.DateNow;
  private _bookingSlots: BookingSlot[];
  private _selectedSlot: BookingSlot;

  private _maxDaySearch = 180;

  constructor(
    shopSettingsService: ShopSettingsService,
    private bookingOrderService: BookingOrderService,
    private bookingSlotChangeStateService: BookingSlotChangeStateService,
  ) {
    super();
    this.shopSettingsService = shopSettingsService;
  }

  initialize(bookingSlotSelectPolicy: BookingSlotSelectPolicyEnum = BookingSlotSelectPolicyEnum.FirstSlot): Observable<any> {
    return of(true)
      .pipe(
        map(_ => {
          this.resetBookingSlots();
          const enabledDaysOfWeek = this.bookingOrderService.enabledDaysOfWeek;
          const disabledDaysOfWeek = [];
          if (enabledDaysOfWeek && enabledDaysOfWeek.length) {
            for (let i = 0; i < 7; i++) {
              if (!(enabledDaysOfWeek.indexOf(i) > -1)) {
                disabledDaysOfWeek.push(i);
              }
            }
          }
          this._disabledDaysOfWeek = disabledDaysOfWeek;

          let date = GlobalSettings.DateNow;
          if (this.isPurchaseOnlyTodayTicket) {
            return date;
          }

          if (!environment.globalSettings.isToday(this.bookingOrderService.selectedBookingDate)) {
            date = this.bookingOrderService.selectedBookingDate || GlobalSettings.DateNow;
          }
          return date;
        }),
        switchMap(date => {
          if (bookingSlotSelectPolicy === BookingSlotSelectPolicyEnum.NearestSlot) {
            return this.selectNearestSlot(GlobalSettings.DateNow);
          }
          return this.dateSelect(date, bookingSlotSelectPolicy)
            .pipe(
              tap(() => {
                if (this.bookingOrderService.selectedBookingSlots && this.bookingOrderService.selectedBookingSlots.length) {
                  this.selectedSlot = this.bookingOrderService.selectedBookingSlots[0];
                }
                if (!this.validBookingSlots || this.validBookingSlots.length < 1) {
                  this.selectedSlot = null;
                }
              }));
        })
      );
  }

  get showBookingSlotsDiagram(): boolean {
    if (!this.validBookingSlots || this.validBookingSlots.length === 0) {
      return false;
    }

    return !this.isEvent && (this.validBookingSlots.length > this._defaultMaxSlotsCountToShowTime);
  }

  get disabledDaysOfWeek(): Array<number> {
    return this._disabledDaysOfWeek;
  }

  get isBirthday(): boolean {
    return this.bookingOrderService.orderType === OrderTypeEnum.Birthday;
  }

  get isGroup(): boolean {
    return this.bookingOrderService.orderType === OrderTypeEnum.Group;
  }

  get isEvent(): boolean {
    return (this.isBirthday || this.isGroup);
  }

  get selectedDate(): Date {
    return this._selectedDate;
  }

  get selectedSlot(): BookingSlot {
    return this._selectedSlot;
  }

  set selectedSlot(bookingSlot: BookingSlot) {
    this._selectedSlot = bookingSlot;
  }

  get bookingSlots(): BookingSlot[] {
    return this._bookingSlots;
  }

  get validBookingSlots(): BookingSlot[] {
    if (this._bookingSlots) {
      const bookingSlots = this._bookingSlots.filter(x => x.nextSlotValid);
      return this.bookingOrderService.filterBookingSlots(bookingSlots, this.selectedDate.getDay());
    }
    return this._bookingSlots;
  }

  private get _selectedSlotIndex(): number {
    if (!(this._selectedSlot && this.validBookingSlots)) {
      return -1;
    }
    return this.validBookingSlots.findIndex(x => x.id === this._selectedSlot.id);
  }

  dateSelect(
    date: Date,
    bookingSlotSelectPolicy: BookingSlotSelectPolicyEnum = BookingSlotSelectPolicyEnum.FirstSlot,
    searchDirection: number = 1,
  ): Observable<boolean> {

    return Observable.create(
      (observable: Subscriber<boolean>) => {


        let newSelectedDate = date;
        if (!this.isDateEnabled(newSelectedDate)) {
          if (!this.isPurchaseOnlyTodayTicket) {
            newSelectedDate = this.searchNearEnabledDate(date, this._maxDaySearch, searchDirection);
          }
          if (!newSelectedDate) {
            // this._selectedDate = null;
            // this._bookingSlots = null;
            // this.selectedSlot = null;
            observable.next(false);
            return;
          }
        }

        this._selectedDate = newSelectedDate;

        this.bookingOrderService.getBookingSlots(this._selectedDate).subscribe(
          bookingSlots => {

            this._bookingSlots = bookingSlots;
            if (this._bookingSlots) {
              this._bookingSlots = this._bookingSlots.sort((a, b) => (a.fromTime < b.fromTime ? -1 : 1));
            }

            this.autoSelectBookingSlot(bookingSlotSelectPolicy);

            observable.next(true);
          }
        );
      });
  }

  getBookingSlotsByTime(date: Date): BookingSlot[] {
    const currentTime = date.getHours() + date.getMinutes() / 60;
    return this.validBookingSlots.filter(slot => slot.fromTime <= currentTime && slot.toTime >= currentTime);
  }

  autoSelectBookingSlot(bookingSlotSelectPolicy: BookingSlotSelectPolicyEnum = BookingSlotSelectPolicyEnum.FirstSlot) {
    if (this.validBookingSlots && this.validBookingSlots.length) {

      if (bookingSlotSelectPolicy === BookingSlotSelectPolicyEnum.LastSlot) {
        this.selectedSlot = this.validBookingSlots[this.validBookingSlots.length - 1];
      }

      if (bookingSlotSelectPolicy === BookingSlotSelectPolicyEnum.FirstSlot) {
        this.selectedSlot = this.validBookingSlots[0];
      }
    } else {
      this.selectedSlot = null;
    }
  }

  get maxSlotsCountToShowTime(): number {
    if (this.showBookingSlotsDiagram) {
      return this._defaultMaxSlotsCountToShowTime;
    }

    if (!this.validBookingSlots) {
      return 0;
    }

    return this.validBookingSlots.length;
  }

  get timeSlots(): BookingSlot[] {
    const showBookingSlotsDiagram = this.showBookingSlotsDiagram;
    const result = new Array<BookingSlot>();
    const bookingSlots = this.validBookingSlots;
    let selectedSlotIndex = this._selectedSlotIndex;
    if(selectedSlotIndex  == -1) {
      this.autoSelectBookingSlot();
      selectedSlotIndex = this._selectedSlotIndex;
    }
    if (selectedSlotIndex > -1 && bookingSlots && bookingSlots.length >= selectedSlotIndex) {
      if (!showBookingSlotsDiagram) {
        selectedSlotIndex = 0;
      }
      let timeSlots = 0;
      for (let i = selectedSlotIndex; i < bookingSlots.length; i++) {
        result.push(bookingSlots[i]);
        timeSlots++;
        if (timeSlots >= this.maxSlotsCountToShowTime) {
          break;
        }
      }
    }
    return result;
  }

  get displayTimeSlots(): BookingSlot[] {
    // tables: filter
    // 1. selectable
    // 2. unique fromTime slots (any)
    if (this.isEvent) {
      const fromTimeToSlots = {};
      this.timeSlots.filter(x => this.canSelectSlot(x)).forEach(timeSlot => {
        if (!fromTimeToSlots[timeSlot.fromTime] || fromTimeToSlots[timeSlot.fromTime].freeQuantity < timeSlot.freeQuantity) {
          fromTimeToSlots[timeSlot.fromTime] = timeSlot;
        }
      });
      const slots = [];
      for (const key in fromTimeToSlots) {
        if (fromTimeToSlots[key]) {
          slots.push(fromTimeToSlots[key]);
        }
      }
      return slots.sort((a, b) => a.fromTime - b.fromTime);
    } else {
      return this.timeSlots;
    }
  }

  canSelectSlot(bookingSlot: BookingSlot, isUserChangeDate = false): boolean {
    let leftMinutes = this._defaultTimeSlotsLength;

    const bookingSlots = isUserChangeDate ? this.bookingSlotChangeStateService.bookingSlots : this.bookingSlots;

    const slotIndex = bookingSlots.findIndex(x => x.id === bookingSlot.id);

    for (let i = slotIndex; i < bookingSlots.length; i++) {

      if (bookingSlots[i].freeQuantity >= this._defaultJumpersCount) {
        leftMinutes = leftMinutes - bookingSlots[i].slotDurationTimeMinutes;
        if (leftMinutes <= 0) {
          break;
        }
        if ((i + 1) === bookingSlots.length) {
          return true;
        }
      } else {
        break;
      }
    }
    return leftMinutes <= 0 ? true : false;
  }

  shiftDay(offset: number): Observable<any> {
    return of(true).pipe(
      switchMap(
        _ => {
          if (!this._selectedDate || !offset) {
            return of(true);
          }

          const newSelectedDate = this.searchNearEnabledDate(this._selectedDate, 6, offset);
          if (newSelectedDate) {
            const selectPolicy = offset > 0 ? BookingSlotSelectPolicyEnum.FirstSlot : BookingSlotSelectPolicyEnum.LastSlot;
            return this.dateSelect(newSelectedDate, selectPolicy);
          }
          return of(true);
        }
      ));
  }

  searchNearEnabledDate(date, maxDays, offset): Date {
    let counter = 0;
    const newSelectedDate = new Date(date);
    while (counter <= maxDays) {
      newSelectedDate.setDate(newSelectedDate.getDate() + offset);
      if (this.isDateEnabled(newSelectedDate)) {
        const selectPolicy = offset > 0 ? BookingSlotSelectPolicyEnum.FirstSlot : BookingSlotSelectPolicyEnum.LastSlot;
        return newSelectedDate;
      }
      counter++;
    }
    return null;
  }

  getSelectedBookingDate(timeSlot: BookingSlot): Date {
    return timeSlot?.getDateTime(this.selectedDate);
  }

  selectNearestSlot(date: Date, offsetDay = 1): Observable<any> {
    return this.dateSelect(date)
      .pipe(
        switchMap(_ => {
          if (!this.selectedSlot) {
            return this.shiftNearestSlot(offsetDay);
          } else {
            return of(true);
          }
        })
      );
  }

  shiftNearestSlot(offsetDay = 1): Observable<any> {
    const isDone = () => {
      if (
        (offsetDay < 0 && environment.globalSettings.isToday(this.selectedDate)) ||
        moment(this.selectedDate).diff(moment(GlobalSettings.DateNow), 'days') > this._maxDaySearch) {
        return true;
      }
      return this.selectedSlot != null;
    };
    return this.shiftDay(offsetDay)
      .pipe(
        map(isDone),
        expand((done) => !done ? this.shiftDay(offsetDay).pipe(map(isDone)) : of(true)),
        skipWhile(done => !done),
        take(1),
      );
  }

  shiftSlot(offset: number): Observable<any> {
    return of(true).pipe(
      switchMap(
        _ => {
          const bookingSlots = this.validBookingSlots;
          if (!(bookingSlots && bookingSlots.length)) {
            return of(false);
          }
          const slots = bookingSlots;
          const newIdx = this._selectedSlotIndex + offset;

          if (newIdx > -1 && newIdx < slots.length) {
            this.selectedSlot = slots[newIdx];
            return of(true);
          }

          return this.shiftDay(newIdx < 0 ? -1 : 1);
        }
      )
    );
  }

  setMonthBefore(): void {
    const newMonth = new Date(this._selectedDate.getFullYear(), this._selectedDate.getMonth() - 1, 1);
    this.dateSelect(newMonth).subscribe();
  }

  setMonthAfter(): void {
    const newMonth = new Date(this._selectedDate.getFullYear(), this._selectedDate.getMonth() + 1, 1);
    this.dateSelect(newMonth).subscribe();
  }

  resetBookingSlots(): void {
    this._bookingSlots = null;
    if (this.bookingOrderService.singleCache && this.bookingOrderService.singleCache.ordersCache) {
      this.bookingOrderService.singleCache.ordersCache.reset();
    }
  }

  isDateEnabled(date: Date): boolean {
    return super.isDateEnabled(date) && this.bookingOrderService.isDateEnabled(date);
  }

  get canShiftDate(): boolean {
    const pickupPoints = this.bookingOrderService.areaAreaItemPack && this.bookingOrderService.areaAreaItemPack.pickupPoints
      ? this.bookingOrderService.areaAreaItemPack.pickupPoints : null;
    if (!pickupPoints || pickupPoints.length === 0) {
      return true;
    }

    const order = this.bookingOrderService.order;
    const productsOrderLines = order.orderLines.filter(x => x.orderLineType === OrderLineType.SingleProduct && x.quantity > 0);
    const selectedDate = this.bookingOrderService.selectedBookingDate;

    if (!selectedDate) {
      return false;
    }

    const pickUpOrderLines = order.orderLines.filter(x => x.pickupPointId && x.quantity > 0);
    let pickupPointId = null;
    for (const pickUpOrderLine of pickUpOrderLines) {
      if (selectedDate && pickUpOrderLine.bookingDate && pickUpOrderLine.bookingDate.getTime() === selectedDate.getTime()) {
        pickupPointId = pickUpOrderLine.pickupPointId;
        break;
      }
    }

    if (productsOrderLines.length && !pickupPointId) {
      for (const productsOrderLine of productsOrderLines) {
        if (selectedDate && productsOrderLine.bookingDate && productsOrderLine.bookingDate.getTime() === selectedDate.getTime()) {
          return false;
        }
      }
      return true;
    }

    return true;
  }

  get isPurchaseOnlyTodayTicket(): boolean {
    return this.bookingOrderService?.areaAreaItemPack?.isPurchaseOnlyTodayTicket;
  }
}
